import { useDict } from '@/composables/fischerPlugin';
import type { SitecoreDictionary } from '@/lib/dictPlugin';
import type { SitecoreNavigation } from '@/sitecoreState';
import { useNavigationStore } from '@/store/pinia/navigation';

export const closeNavigationFlyoutFunction = (
  navigationStore: ReturnType<typeof useNavigationStore>,
) => {
  navigationStore.setShowFlyout(false);
  navigationStore.setNavIndex(-1);
  navigationStore.setGroupData(undefined);
  navigationStore.setSubElements([]);
  navigationStore.setShowBrandArea(true);
};

export const showMobileSubNavigationFunction = (
  navigationStore: ReturnType<typeof useNavigationStore>,
  isSM: boolean,
) => {
  navigationStore.setShowMobileFirstLvl(false);
  if (isSM) {
    navigationStore.setShowFlyout(true);
  } else {
    navigationStore.setShowMobileSubLvl(true);
  }
};

export const hideMobileSubNavigationFunction = (
  navigationStore: ReturnType<typeof useNavigationStore>,
  isSM: boolean,
) => {
  navigationStore.setIsCountrySelect(false);
  if (isSM) {
    navigationStore.setShowFlyout(false);
  } else {
    navigationStore.setShowMobileSubLvl(false);
  }
};

export const resetNavigationDataFunction = (
  navigationStore: ReturnType<typeof useNavigationStore>,
) => {
  navigationStore.setSubElements([]);
  navigationStore.setGroupData(undefined);
  navigationStore.setNavIndex(-1);
};

export const backToMobileFirstLevelFunction = (
  navigationStore: ReturnType<typeof useNavigationStore>,
) => {
  resetNavigationDataFunction(navigationStore);
  navigationStore.setShowFlyout(false);
  navigationStore.setShowMobileSubLvl(false);
  navigationStore.setShowMobileFirstLvl(true);
  navigationStore.setIsCountrySelect(false);
};

export type SubNavigation = {
  groupList: SitecoreNavigation[];
  groupLabel?: string;
  id: string;
};

export const getNavigationGroupsFunction = (navigationSubElements: SitecoreNavigation[]) => {
  const subNavigation: SubNavigation[] = [];
  let subList: SitecoreNavigation[] = [];

  navigationSubElements.forEach((subElement, idx) => {
    if (subElement.isGroupPage) {
      if (subList.length > 0) {
        subNavigation.push({ groupList: subList, id: `sublist_${idx}` });
        subList = [];
      }
      subNavigation.push({
        groupList: subElement.subElements,
        groupLabel: subElement.label,
        id: subElement.id,
      });
    } else {
      subList.push(subElement);
    }
  });

  if (subList.length > 0) {
    subNavigation.push({ groupList: subList, id: 'subNavi' });
  }

  return subNavigation;
};

export interface GroupData {
  labelSubNavigation?: string | null;
  label: string;
}
export const navigationLayerTitleFunction = (
  groupData: GroupData | undefined,
  dict: SitecoreDictionary,
) => {
  return groupData?.labelSubNavigation
    ? groupData.labelSubNavigation
    : dict.get('global-Labels.Navigation.Overview', [groupData?.label || '']);
};

export const useNavigation = () => {
  const navigationStore = useNavigationStore();
  const dict = useDict();

  const closeNavigationFlyout = () => closeNavigationFlyoutFunction(navigationStore);

  const showMobileSubNavigation = (isSM: boolean) =>
    showMobileSubNavigationFunction(navigationStore, isSM);

  const hideMobileSubNavigation = (isSM: boolean) =>
    hideMobileSubNavigationFunction(navigationStore, isSM);

  const resetNavigationData = () => resetNavigationDataFunction(navigationStore);

  const backToMobileFirstLevel = () => backToMobileFirstLevelFunction(navigationStore);

  const navigationLayerTitle = (groupData: GroupData | undefined) =>
    navigationLayerTitleFunction(groupData, dict);

  return {
    backToMobileFirstLevel,
    closeNavigationFlyout,
    getNavigationGroups: getNavigationGroupsFunction,
    hideMobileSubNavigation,
    navigationLayerTitle,
    resetNavigationData,
    showMobileSubNavigation,
  };
};
